import { useCallback, useMemo } from 'react';

import { useMarketCacheStore } from '@/feature/event-details-sbk/hooks/use-market-cache';
import { filterMarketsForProduct } from '@/feature/event-details-sbk/utils/is-supplier-for-product';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { useSubscribePublic } from '@/utils/websocket/hooks';
import { MarketUpdateMessage, OddsUpdateMessageOption } from '@/utils/websocket/types';
import { runStoreUpdate } from '@/utils/zustand';

/**
 * Subscribe to 'league:{leagueId}:gamelines' channel and listens for `market_update` messages.
 * Broadcasts received markets to all subscribed components via the market update bus.
 */
export const useSubscribeLeaguePopularMarketUpdate = (leagueId: string, enabled: boolean = true) => {
    const handleOddsUpdate = useMarketCacheStore(state => state.actions.handleOddsUpdate);
    const updateMainLineMarkets = useMarketCacheStore(state => state.actions.updateMainLineMarkets);
    const isPredictions = useIsPredictionsProduct();

    const handleMarketUpdate = useCallback(
        (incomingMessages: MarketUpdateMessage[]) => {
            const messages = filterMarketsForProduct(incomingMessages, isPredictions);
            const oddsUpdateMessages: OddsUpdateMessageOption[] = messages.flatMap(message =>
                message.options.map(option => ({
                    id: option.id,
                    marketId: message.id,
                    odds: option.odds,
                    status: option.status,
                    isMainLine: message.is_main_line,
                    published: message.published,
                    modifiers: [],
                }))
            );
            runStoreUpdate(() => {
                handleOddsUpdate(oddsUpdateMessages);
                updateMainLineMarkets(messages);
            });
        },
        [handleOddsUpdate, updateMainLineMarkets, isPredictions]
    );

    const params = useMemo(() => ({ leagueId }), [leagueId]);
    useSubscribePublic<'leaguePopularMarketUpdate'>(
        'league:{leagueId}:gamelines',
        params,
        'market_update',
        handleMarketUpdate,
        enabled
    );
};
